import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { AssistantSidebar } from "./AssistantSidebar";

export interface AssistantLauncherProps {
  /** Client to scope the assistant to. Omit outside a project — it can still
   *  resolve a client from the conversation via its tools. */
  projectId?: string;
  projectName?: string;
  /** Marketplace campaign to scope to. Narrower than projectId when both are set. */
  campaignId?: string;
  /** Lifted above the mobile bottom nav on shells that have one. */
  offsetForMobileNav?: boolean;
}

/**
 * Floating button plus the assistant panel, as one drop-in unit.
 *
 * Kept together so every shell that wants the assistant mounts one component
 * and cannot end up with a panel nothing can open — which is exactly what
 * happened when the button and the panel were wired separately per screen.
 */
export function AssistantLauncher({
  projectId,
  projectName,
  campaignId,
  offsetForMobileNav = true,
}: AssistantLauncherProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title={t("assistant.title")}
          aria-label={t("assistant.title")}
          className={cn(
            "fixed right-6 z-40 flex h-12 items-center gap-2 rounded-pill px-4",
            offsetForMobileNav ? "bottom-20 md:bottom-6" : "bottom-6",
            // The primary button's own gradient and lift. It used to carry the
            // avatar gradient and a hover scale — nothing in this system scales
            // on hover, and the launcher is a primary action like any other.
            "bg-gradient-to-r from-steel-600 to-steel-700 text-white shadow-button-primary",
            "transition-[background,transform,box-shadow] duration-200",
            "hover:-translate-y-px hover:from-steel-500 hover:to-steel-600",
          )}
        >
          <MessageCircle className="size-5 shrink-0" />
          <span className="hidden text-control font-semibold sm:inline">
            {t("assistant.title")}
          </span>
        </button>
      )}

      <AssistantSidebar
        open={open}
        onClose={() => setOpen(false)}
        projectId={projectId}
        projectName={projectName}
        campaignId={campaignId}
      />
    </>
  );
}
